import React, { useEffect, useState, useRef } from 'react';
import { MESSAGE_TYPE, useFloatMessage } from '../../../Providers/FloatMessage';
import { SecondaryBase } from '../../Commons/SecondaryBase';
import { Confirm } from '../../../Commons/Modals/Confirm';
import {
    GetAdminParticipants,
    GetAdminStats,
    GetAdminQuestions,
    GetAdminConfig,
    UpdateAdminConfig,
    SetOverlayVisibilityDL,
    SetOverlayOffsetDL,
    UndisqualifyParticipant,
    ResetDynamic,
    GetExportDataset,
    ExportParticipantsToExcel,
    formatMexicoDate
} from '../../../../utilities/BL/MvpXolosZone';

import { MvpXolosZoneConfigModal } from './MvpXolosZoneConfigModal';
import { MvpXolosZoneQuestionsModal } from './MvpXolosZoneQuestionsModal';
import { MvpXolosZoneDisqualifyModal } from './MvpXolosZoneDisqualifyModal';
import { MvpXolosZoneCheckinModal } from './MvpXolosZoneCheckinModal';

function throttle(func, limit) {
    let inThrottle;
    return function(...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
}

export function MvpXolosZoneBoard({ getOutOfTheDynamic }) {
    const { showMessage } = useFloatMessage();

    // Data States
    const [participants, setParticipants] = useState([]);
    const [questions, setQuestions] = useState([]);
    const [config, setConfig] = useState({
        matchTitle: 'Club Tijuana Femenil vs Rival',
        matchCode: 'TIJ',
        passCodePrefix: 'XOLO',
        maxWinners: 50,
        isTriviaActive: true,
        offsetY: 0,
        passViewMode: 'COMPLETE',
        showPassQr: true,
        showPassUserData: false,
        showPassLocationNote: false
    });
    const [stats, setStats] = useState({
        qrScan: 0,
        pageView: 0,
        formStart: 0,
        formComplete: 0,
        quizStart: 0,
        quizComplete: 0,
        gameStart: 0,
        gameComplete: 0,
        passesIssued: 0,
        passesAttended: 0,
        passesDisqualified: 0,
        passesRecovered: 0
    });

    const [isLoading, setIsLoading] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Live Overlay State
    const [offsetY, setOffsetY] = useState(0);
    const [isOverlayVisible, setIsOverlayVisible] = useState(false);
    const [isTriviaActive, setIsTriviaActive] = useState(true);

    // Modals Control
    const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
    const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
    const [isCheckinModalOpen, setIsCheckinModalOpen] = useState(false);
    const [disqualifyTarget, setDisqualifyTarget] = useState(null);

    // Advanced Section State
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

    // Search and Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [scoreFilter, setScoreFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all'); // all, winners, attended, non-winners, disqualified

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // Confirm Modal States
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        action: null,
        textYes: 'Sí, confirmar',
        textNo: 'Cancelar'
    });

    const throttledUpdatePosition = useRef(
        throttle(async (val) => {
            await SetOverlayOffsetDL(val);
            await UpdateAdminConfig({ ...config, offsetY: val });
        }, 150)
    ).current;

    const loadInitialData = async () => {
        setIsLoading(true);
        try {
            const [partRes, statsRes, questionsRes, configRes] = await Promise.all([
                GetAdminParticipants(),
                GetAdminStats(),
                GetAdminQuestions(),
                GetAdminConfig()
            ]);

            if (partRes.success && partRes.data) {
                setParticipants(partRes.data);
            }

            if (statsRes.success && statsRes.data) {
                setStats(statsRes.data);
            }

            if (questionsRes.success && questionsRes.data) {
                setQuestions(questionsRes.data);
            }

            if (configRes.success && configRes.data) {
                setConfig(configRes.data);
                setOffsetY(configRes.data.offsetY || 0);
                setIsTriviaActive(configRes.data.isTriviaActive ?? true);
                setIsOverlayVisible(configRes.data.isOverlayVisible ?? false);
            }

            setLastUpdated(new Date().toLocaleString('es-MX', { hour12: false }) + ' hrs');
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error al sincronizar datos del panel.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadInitialData();
    }, []);

    const handleReturn = () => {
        if (getOutOfTheDynamic) {
            getOutOfTheDynamic();
        } else {
            window.location.href = '/admin/dashboard';
        }
    };

    const handlePositionChange = (e) => {
        const val = parseInt(e.target.value, 10) || 0;
        setOffsetY(val);
        throttledUpdatePosition(val);
    };

    const triggerToggleOverlayVisibility = (nextState) => {
        setConfirmConfig({
            message: `¿Estás seguro de que deseas ${nextState ? 'MOSTRAR' : 'OCULTAR'} el banner de MVP Xolos Femenil en pantallas?`,
            textYes: nextState ? 'Sí, mostrar' : 'Sí, ocultar',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando visibilidad del overlay...' });
                await SetOverlayVisibilityDL(nextState);
                await UpdateAdminConfig({ ...config, isOverlayVisible: nextState });
                setIsOverlayVisible(nextState);
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: `El banner ahora está ${nextState ? 'visible' : 'oculto'} en pantalla.`
                });
                return { success: true };
            }
        });
        setShowConfirm(true);
    };

    const triggerToggleActiveTrivia = (nextState) => {
        setConfirmConfig({
            message: `¿Estás seguro de que deseas ${nextState ? 'ABRIR' : 'CERRAR'} la participación en la trivia para los aficionados?`,
            textYes: nextState ? 'Sí, abrir' : 'Sí, cerrar',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando estado de la trivia...' });
                const res = await UpdateAdminConfig({ ...config, isTriviaActive: nextState });
                if (res.success) {
                    setIsTriviaActive(nextState);
                    setConfig(prev => ({ ...prev, isTriviaActive: nextState }));
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: `Trivia ${nextState ? 'abierta (recibiendo registros)' : 'cerrada (bloqueada)'} con éxito.`
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
                    return { success: false };
                }
            }
        });
        setShowConfirm(true);
    };

    const triggerUndisqualify = (participant) => {
        setConfirmConfig({
            message: `¿Deseas quitar la descalificación y restituir a "${participant.name}" (${participant.passCode})?`,
            textYes: 'Sí, quitar descalificación',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Restituyendo participante...' });
                const res = await UndisqualifyParticipant(participant.id);
                if (res.success) {
                    setParticipants(prev => prev.map(p => 
                        p.id === participant.id 
                            ? { ...p, isDisqualified: false, disqualifiedReason: null } 
                            : p
                    ));
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: `Descalificación removida para ${participant.name}.`
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
                    return { success: false };
                }
            }
        });
        setShowConfirm(true);
    };

    const triggerResetDynamic = () => {
        setConfirmConfig({
            message: '⚠️ ¿Estás COMPLETAMENTE SEGURO de que deseas reiniciar la dinámica? Esto archivará a todos los participantes registrados y reiniciará el contador de ganadores en 0 para el nuevo partido.',
            textYes: 'Sí, reiniciar dinámica',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reiniciando dinámica...' });
                const res = await ResetDynamic();
                if (res.success) {
                    setParticipants([]);
                    setIsOverlayVisible(false);
                    setStats({
                        qrScan: 0,
                        pageView: 0,
                        formStart: 0,
                        formComplete: 0,
                        quizStart: 0,
                        quizComplete: 0,
                        passesIssued: 0,
                        passesAttended: 0,
                        passesDisqualified: 0,
                        passesRecovered: 0
                    });
                    setLastUpdated(new Date().toLocaleString('es-MX', { hour12: false }) + ' hrs');
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: '¡La dinámica ha sido reiniciada con éxito!'
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
                    return { success: false };
                }
            }
        });
        setShowConfirm(true);
    };

    const handleExportExcel = async () => {
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando dataset y reporte Excel...' });
        try {
            const exportRes = await GetExportDataset();
            const dataset = exportRes?.data || (exportRes?.participants ? exportRes : null);
            if (dataset && dataset.participants && dataset.participants.length > 0) {
                const ok = ExportParticipantsToExcel(dataset, questions, config.matchTitle);
                if (ok) {
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡Reporte Excel de 4 hojas descargado correctamente!' });
                }
                return;
            }
        } catch (e) {
            console.warn("Fallback a participantes locales para exportación Excel:", e);
        }

        if (participants.length === 0) {
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'No hay registros para exportar.' });
            return;
        }
        const ok = ExportParticipantsToExcel(participants, questions, config.matchTitle);
        if (ok) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: '¡Reporte Excel descargado correctamente!' });
        }
    };

    // Client-side filtering logic
    const filteredParticipants = participants.filter(p => {
        const matchesSearch = !searchTerm || 
            p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.phone.includes(searchTerm) ||
            (p.passCode && p.passCode.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesScore = scoreFilter === 'all' || 
            (config.gameType === 'TRIVIA' 
                ? p.score.toString() === scoreFilter 
                : p.score >= parseInt(scoreFilter, 10));

        let matchesStatus = true;
        if (statusFilter === 'winners') {
            matchesStatus = p.isWinner && !p.isDisqualified;
        } else if (statusFilter === 'attended') {
            matchesStatus = p.hasAttended;
        } else if (statusFilter === 'non-winners') {
            matchesStatus = !p.isWinner;
        } else if (statusFilter === 'disqualified') {
            matchesStatus = p.isDisqualified;
        }

        return matchesSearch && matchesScore && matchesStatus;
    });

    // Pagination logic
    const totalRegistries = filteredParticipants.length;
    const totalPages = Math.ceil(totalRegistries / pageSize) || 1;
    const verifiedPage = Math.min(currentPage, totalPages);
    const startIndex = (verifiedPage - 1) * pageSize;
    const paginatedParticipants = filteredParticipants.slice(startIndex, startIndex + pageSize);

    const winnersCount = participants.filter(p => p.isWinner && !p.isDisqualified).length;
    const attendedCount = participants.filter(p => p.hasAttended).length;

    return (
        <SecondaryBase actionToExecute={handleReturn}>
            {/* Modal de Confirmación */}
            {showConfirm && (
                <Confirm 
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.action}
                    textYesButton={confirmConfig.textYes}
                    textNoButton={confirmConfig.textNo}
                    setShowModal={setShowConfirm}
                />
            )}

            {/* Modal de Configuración de Jornada */}
            <MvpXolosZoneConfigModal 
                isOpen={isConfigModalOpen}
                onClose={() => setIsConfigModalOpen(false)}
                onConfigUpdated={(newCfg) => {
                    setConfig(newCfg);
                    setIsTriviaActive(newCfg.isTriviaActive);
                    setOffsetY(newCfg.offsetY);
                }}
                onOpenQuestionsModal={() => setIsQuestionsModalOpen(true)}
            />

            {/* Modal de Preguntas y Opciones */}
            <MvpXolosZoneQuestionsModal 
                isOpen={isQuestionsModalOpen}
                onClose={() => setIsQuestionsModalOpen(false)}
                onQuestionsUpdated={loadInitialData}
            />

            {/* Modal de Descalificación con Motivo */}
            <MvpXolosZoneDisqualifyModal 
                isOpen={!!disqualifyTarget}
                participant={disqualifyTarget}
                onClose={() => setDisqualifyTarget(null)}
                onDisqualified={(id, reason) => {
                    setParticipants(prev => prev.map(p => 
                        p.id === id 
                            ? { ...p, isDisqualified: true, disqualifiedReason: reason } 
                            : p
                    ));
                }}
            />

            {/* Modal de Check-in y Validación en Cancha para Staff */}
            <MvpXolosZoneCheckinModal
                isOpen={isCheckinModalOpen}
                onClose={() => {
                    setIsCheckinModalOpen(false);
                    loadInitialData();
                }}
                totalWinners={config.maxWinners || 50}
                initialAttended={attendedCount}
                onCheckinSuccess={(checkinRes) => {
                    if (checkinRes.participantId) {
                        setParticipants(prev => prev.map(p => 
                            p.id === checkinRes.participantId
                                ? { ...p, hasAttended: true, attendedAtUtc: checkinRes.attendedAtUtc }
                                : p
                        ));
                    }
                }}
            />

            <div className="container-fluid px-4 py-3 MvpXolosZoneBoard">
                {/* Header con Título y Botones de Modales */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4 border-bottom pb-3">
                    <div>
                        <h1 className="h3 fw-bold text-dark mb-1">
                            <i className="bi bi-gift-fill text-danger me-2"></i>
                            MVP Xolos Femenil
                        </h1>
                        <p className="text-secondary small mb-0 d-flex align-items-center gap-2 flex-wrap">
                            <span>{config.matchTitle} — Prefijo: <strong>{config.passCodePrefix || 'XOLO'}</strong> | Rival: <strong>{config.matchCode || 'TIJ'}</strong></span>
                            <span className="badge bg-dark text-white fw-semibold">
                                {config.gameType === 'TRIVIA' ? '❓ Trivia' : config.gameType === 'SEQUENCE' ? '🎮 Secuencia' : config.gameType === 'SCRATCH' ? '🎟️ Rasca y Gana' : '🔨 Aplastuzo'}
                            </span>
                            <span className="badge bg-danger text-white fw-semibold">
                                {config.gameType === 'SEQUENCE'
                                    ? `🎯 Secuencia Perfecta (${config.timeLimitSeconds || 6} pasos)`
                                    : config.winnerSelectionMode === 'PARTICIPATION'
                                    ? '🎟️ Participación Directa'
                                    : config.winnerSelectionMode === 'TRIVIA_PERFECT'
                                    ? '🧠 Trivia Perfecta'
                                    : `🎯 Meta ≥ ${config.minScoreRequired ?? 25} pts`}
                            </span>
                        </p>
                    </div>

                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <button 
                            type="button" 
                            className="btn btn-success fw-bold d-flex align-items-center gap-2 shadow-sm"
                            onClick={() => setIsCheckinModalOpen(true)}
                        >
                            <i className="bi bi-qr-code-scan fs-6"></i>
                            Escanear Pases / Check-in
                            <span className="badge bg-white text-success fw-bold ms-1">
                                {attendedCount} / {config.maxWinners || 50}
                            </span>
                        </button>
                        <button 
                            type="button" 
                            className="btn btn-outline-dark fw-bold d-flex align-items-center gap-2"
                            onClick={() => setIsConfigModalOpen(true)}
                        >
                            <i className="bi bi-gear-fill text-danger"></i>
                            Configuración de jornada
                        </button>
                    </div>
                </div>

                {/* Card de Configuración Básica y Control de Overlay */}
                <div className="row g-4 mb-4">
                    <div className="col-12">
                        <div className="bg-light p-4 rounded-3 border">
                            <p className="fs-4 fw-bold text-center mb-3">Control de Pantalla y Overlay</p>

                            <div className="mb-4 text-center">
                                <p className="fs-5 mb-2">
                                    Estatus: <span className={`fw-bold ${isOverlayVisible ? 'text-success' : 'text-danger'}`}>
                                        {isOverlayVisible ? 'El banner se está mostrando en pantalla' : 'El banner está oculto'}
                                    </span>
                                </p>
                                <button
                                    type="button"
                                    onClick={() => triggerToggleOverlayVisibility(!isOverlayVisible)}
                                    className={`btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${
                                        isOverlayVisible 
                                        ? 'btn-outline-danger' 
                                        : 'btn-outline-success'
                                    }`}
                                >
                                    <i className={`bi ${isOverlayVisible ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                                    {isOverlayVisible ? 'Ocultar banner de pantallas' : 'Mostrar banner en pantallas'}
                                </button>
                            </div>

                            <span className="d-block text-center text-dark small fw-bold mb-2">
                                Vista previa en pantalla (Modo Invitación / QR):
                            </span>
                            <div className="bg-dark text-white p-3 rounded-2 text-center small mb-4 shadow-sm" style={{ minHeight: '65px' }}>
                                <div className="d-flex justify-content-around align-items-center">
                                    <i className="bi bi-qr-code fs-4 text-warning"></i>
                                    <div>
                                        <span className="d-block text-warning fw-bold text-uppercase" style={{ fontSize: '0.7rem' }}>
                                            🎉 ¡GANA UNA PASE AL MVP XOLOS FEMENIL! 🎉
                                        </span>
                                        <span className="fw-semibold">Escanea los códigos QR para participar en la dinámica</span>
                                    </div>
                                    <i className="bi bi-qr-code fs-4 text-warning"></i>
                                </div>
                            </div>

                            {/* Tarjeta de Monitoreo de Pases Dorados */}
                            <div className="card p-3 border-0 rounded-3 shadow-sm bg-white text-center">
                                <div className="row g-3 align-items-center">
                                    <div className="col-12 col-md-6 border-end-md">
                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                            <i className="bi bi-award-fill text-warning fs-3"></i>
                                            <div className="text-start">
                                                <div className="text-muted small fw-semibold">Pases Otorgados</div>
                                                <div className="fs-4 fw-bold text-dark">
                                                    {winnersCount} <span className="text-muted fs-6">/ {config.maxWinners || 50}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <div className="d-flex align-items-center justify-content-center gap-2">
                                            <i className="bi bi-check-circle-fill text-success fs-3"></i>
                                            <div className="text-start">
                                                <div className="text-muted small fw-semibold">Pases Ingresados a Cancha</div>
                                                <div className="fs-4 fw-bold text-success">
                                                    {attendedCount} <span className="text-muted fs-6">/ {winnersCount}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Métricas en Tiempo Real (Funnel) */}
                <p className="fs-4 text-center mt-4 mb-1 fw-bold">Estadísticas en Tiempo Real</p>
                <div className="text-center mb-3">
                    <button 
                        type="button" 
                        className="btn btn-outline-primary mb-1"
                        onClick={loadInitialData}
                        disabled={isLoading}
                    >
                        <i className="bi bi-arrow-clockwise me-1"></i> Recargar estadísticas y listado
                    </button>
                    {lastUpdated && (
                        <div className="text-muted small mt-1">
                            <i className="bi bi-clock-history me-1"></i> Última actualización: {lastUpdated}
                        </div>
                    )}
                </div>

                <div className="row g-3 mb-4 text-center">
                    <div className="col-6 col-md-3 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-qr-code text-danger fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">Escaneos QR</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.qrScan ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-globe text-primary fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">Ingresos Página</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.pageView ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-pencil-square text-warning fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">Inicios Registro</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.formStart ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-check-circle-fill text-success fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">Registros Completos</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.formComplete ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-play-fill text-info fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">
                                {config.gameType === 'TRIVIA' ? 'Inicios Trivia' : 'Partidas Iniciadas'}
                            </div>
                            <div className="fs-3 fw-bold text-dark mt-1">
                                {config.gameType === 'TRIVIA' 
                                    ? (stats.quizStart ?? 0) 
                                    : (stats.gameStart !== undefined && stats.gameStart > 0 ? stats.gameStart : (stats.quizStart ?? 0))}
                            </div>
                        </div>
                    </div>
                    <div className="col-6 col-md-3 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-flag-fill text-dark fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">
                                {config.gameType === 'TRIVIA' ? 'Trivias Enviadas' : 'Partidas Finalizadas'}
                            </div>
                            <div className="fs-3 fw-bold text-dark mt-1">
                                {config.gameType === 'TRIVIA' 
                                    ? (stats.quizComplete ?? 0) 
                                    : (stats.gameComplete !== undefined && stats.gameComplete > 0 ? stats.gameComplete : (stats.quizComplete ?? 0))}
                            </div>
                        </div>
                    </div>
                    <div className="col-12 col-md-6 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-arrow-repeat text-info fs-3 mb-1"></i>
                            <div className="text-secondary small fw-semibold">Pases Recuperados</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.passesRecovered ?? 0}</div>
                        </div>
                    </div>
                </div>

                {/* Tabla y Listado de Participantes */}
                <div className="row g-4">
                    <div className="col-12">
                        <div className="card shadow-sm border-0 rounded-3 p-4 bg-white">
                            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2 flex-wrap gap-2">
                                <h3 className="h5 fw-bold text-dark mb-0">
                                    <i className="bi bi-list-stars me-2 text-danger"></i>
                                    Listado de Participantes
                                </h3>
                                <span className="px-3 py-1.5 fw-bold bg-light rounded-pill border small">
                                    Total: {totalRegistries} participantes
                                </span>
                            </div>

                            {/* Buscador y Filtros */}
                            <div className="row g-2 mb-3 bg-light p-3 rounded border">
                                <div className="col-12 col-md-5">
                                    <label className="form-label small fw-semibold text-secondary mb-1">Buscar participante</label>
                                    <div className="input-group">
                                        <span className="input-group-text bg-white"><i className="bi bi-search"></i></span>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            placeholder="Nombre, correo, teléfono o folio #XOLO..." 
                                            value={searchTerm}
                                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                        />
                                    </div>
                                </div>
                                <div className="col-6 col-md-3">
                                    <label className="form-label small fw-semibold text-secondary mb-1">
                                        {config.gameType === 'TRIVIA' ? 'Aciertos' : 'Puntuación'}
                                    </label>
                                    <select 
                                        className="form-select"
                                        value={scoreFilter}
                                        onChange={(e) => { setScoreFilter(e.target.value); setCurrentPage(1); }}
                                    >
                                        <option value="all">Todos</option>
                                        {config.gameType === 'TRIVIA' ? (
                                            <>
                                                <option value={questions.length.toString()}>Perfectos ({questions.length} / {questions.length})</option>
                                                <option value="4">4 aciertos</option>
                                                <option value="3">3 aciertos</option>
                                                <option value="2">2 aciertos</option>
                                                <option value="1">1 acierto</option>
                                                <option value="0">0 aciertos</option>
                                            </>
                                        ) : (
                                            <>
                                                <option value="35">≥ 35 pts</option>
                                                <option value="30">≥ 30 pts</option>
                                                <option value="25">≥ 25 pts (Meta)</option>
                                                <option value="20">≥ 20 pts</option>
                                                <option value="10">≥ 10 pts</option>
                                            </>
                                        )}
                                    </select>
                                </div>
                                <div className="col-6 col-md-4">
                                    <label className="form-label small fw-semibold text-secondary mb-1">Estatus del Aficionado</label>
                                    <select 
                                        className="form-select"
                                        value={statusFilter}
                                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                                    >
                                        <option value="all">Todos</option>
                                        <option value="winners">Ganadores (Top 50)</option>
                                        <option value="attended">Ingresados a Cancha</option>
                                        <option value="non-winners">No Ganadores</option>
                                        <option value="disqualified">Descalificados</option>
                                    </select>
                                </div>
                            </div>

                            <button 
                                type="button" 
                                className="btn btn-outline-success d-block ms-auto mb-3 fw-semibold" 
                                onClick={handleExportExcel}
                            >
                                <i className="bi bi-file-earmark-excel-fill me-1"></i> 
                                Exportar datos a Excel (.xlsx)
                            </button>

                            {isLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-danger" role="status">
                                        <span className="visually-hidden">Cargando...</span>
                                    </div>
                                    <p className="text-secondary small mt-2">Cargando participantes...</p>
                                </div>
                            ) : paginatedParticipants.length > 0 ? (
                                <div>
                                    <div className="table-responsive" style={{ maxHeight: '550px', overflowY: 'auto' }}>
                                        <table className="table align-middle table-hover border-secondary-subtle">
                                            <thead className="table-light sticky-top" style={{ zIndex: 10 }}>
                                                <tr className="small text-secondary fw-bold text-uppercase">
                                                    <th scope="col">Aficionado / Folio</th>
                                                    <th scope="col">Contacto</th>
                                                    <th scope="col" className="text-center">Dinámica</th>
                                                    <th scope="col" className="text-center">
                                                        {config.gameType === 'TRIVIA' ? 'Respuestas Correctas' : 'Puntos Obtenidos'}
                                                    </th>
                                                    <th scope="col" className="text-center">Acceso Cancha</th>
                                                    <th scope="col">Fecha Registro</th>
                                                    <th scope="col" className="text-center">Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedParticipants.map((p) => {
                                                    const cleanPhone = p.phone ? p.phone.replace(/\D/g, '') : '';
                                                    return (
                                                        <tr key={p.id} className={p.isDisqualified ? 'table-secondary opacity-75' : p.isWinner ? 'table-warning' : ''}>
                                                            <td>
                                                                <div className="fw-bold text-dark d-flex align-items-center gap-1 flex-wrap">
                                                                    <span>{p.name}</span>
                                                                    {p.wasRestituted && (
                                                                        <span 
                                                                            className="badge bg-info-subtle text-info border border-info rounded-pill fw-bold" 
                                                                            style={{ fontSize: '0.6rem' }}
                                                                            title={`Restituido por ${p.restitutedBy || 'Team Interaktia'} el ${formatMexicoDate(p.restitutedAtUtc)}`}
                                                                        >
                                                                            ♻️ RESTITUIDO
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {p.isDisqualified ? (
                                                                    <div className="mt-1">
                                                                        <span className="badge bg-danger text-white rounded-pill fw-bold" style={{ fontSize: '0.65rem' }}>
                                                                            ⛔ DESCALIFICADO
                                                                        </span>
                                                                        <small className="text-danger d-block mt-0.5" style={{ fontSize: '0.7rem' }}>
                                                                            Motivo: {p.disqualifiedReason || 'Sin motivo especificado'}
                                                                        </small>
                                                                        {p.disqualifiedBy && (
                                                                            <small className="text-muted d-block" style={{ fontSize: '0.65rem' }}>
                                                                                Por: {p.disqualifiedBy}
                                                                            </small>
                                                                        )}
                                                                    </div>
                                                                ) : p.isWinner ? (
                                                                    <div className="d-flex align-items-center gap-1 mt-1 flex-wrap">
                                                                        <span className="badge bg-warning text-dark rounded-pill fw-bold" style={{ fontSize: '0.65rem' }}>
                                                                            🏆 GANADOR #{p.winnerRank}
                                                                        </span>
                                                                        <span className="badge bg-dark text-warning font-monospace" style={{ fontSize: '0.65rem' }}>
                                                                            {p.passCode}
                                                                        </span>
                                                                    </div>
                                                                ) : null}
                                                            </td>
                                                            <td>
                                                                <div className="small text-secondary">
                                                                    <i className="bi bi-envelope me-1"></i>{p.email}
                                                                </div>
                                                                <div className="small text-secondary">
                                                                    <a 
                                                                        href={`https://wa.me/52${cleanPhone}`} 
                                                                        target="_blank" 
                                                                        rel="noreferrer"
                                                                        className="text-decoration-none text-success fw-semibold"
                                                                    >
                                                                        <i className="bi bi-whatsapp me-1"></i>{p.phone}
                                                                    </a>
                                                                </div>
                                                            </td>
                                                            <td className="text-center">
                                                                <span className={`badge ${
                                                                    (p.gameType || config.gameType) === 'TRIVIA'
                                                                        ? 'bg-primary-subtle text-primary border border-primary'
                                                                        : (p.gameType || config.gameType) === 'SEQUENCE'
                                                                        ? 'bg-info-subtle text-info-emphasis border border-info'
                                                                        : (p.gameType || config.gameType) === 'SCRATCH'
                                                                        ? 'bg-secondary-subtle text-secondary border border-secondary'
                                                                        : 'bg-warning-subtle text-dark border border-warning'
                                                                } rounded-pill fw-bold`} style={{ fontSize: '0.72rem' }}>
                                                                    {(p.gameType || config.gameType) === 'TRIVIA' 
                                                                        ? '❓ Trivia' 
                                                                        : (p.gameType || config.gameType) === 'SEQUENCE' 
                                                                        ? '🎮 Secuencia' 
                                                                        : (p.gameType || config.gameType) === 'SCRATCH' 
                                                                        ? '🎟️ Rasca' 
                                                                        : '🔨 Aplastuzo'}
                                                                </span>
                                                            </td>
                                                            <td className="text-center fw-bold fs-5 text-danger">
                                                                {(p.gameType || config.gameType) === 'TRIVIA' ? (
                                                                    <>{p.score} <span className="text-muted fs-6">/ {p.totalQuestions || questions.length}</span></>
                                                                ) : (p.gameType || config.gameType) === 'SEQUENCE' ? (
                                                                    <>{p.score} <span className="text-muted fs-6">/ {p.totalQuestions || config.timeLimitSeconds || 6} pasos</span></>
                                                                ) : (
                                                                    <>{p.score} <span className="text-muted fs-6">pts</span></>
                                                                )}
                                                            </td>
                                                            <td className="text-center">
                                                                {p.hasAttended ? (
                                                                    <span className="badge bg-success-subtle text-success border border-success fw-bold px-2 py-1">
                                                                        <i className="bi bi-check2-circle me-1"></i> Ingresó
                                                                        <div className="text-muted fw-normal" style={{ fontSize: '0.65rem' }}>
                                                                            {formatMexicoDate(p.attendedAtUtc)}
                                                                        </div>
                                                                        {p.checkedInBy && (
                                                                            <div className="text-secondary" style={{ fontSize: '0.6rem' }}>
                                                                                Por: {p.checkedInBy}
                                                                            </div>
                                                                        )}
                                                                    </span>
                                                                ) : p.isWinner ? (
                                                                    <span className="badge bg-light text-muted border px-2 py-1">
                                                                        Pendiente
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-muted small">-</span>
                                                                )}
                                                            </td>
                                                            <td className="small text-muted">
                                                                {formatMexicoDate(p.createdAtUtc)}
                                                            </td>
                                                            <td className="text-center">
                                                                <div className="d-flex justify-content-center gap-1">
                                                                    {p.isDisqualified ? (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => triggerUndisqualify(p)}
                                                                            className="btn btn-sm btn-outline-success fw-semibold"
                                                                            title="Quitar descalificación (perdonar)"
                                                                        >
                                                                            <i className="bi bi-arrow-counterclockwise me-1"></i>
                                                                            Restituir
                                                                        </button>
                                                                    ) : (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setDisqualifyTarget(p)}
                                                                            className="btn btn-sm btn-outline-danger fw-semibold"
                                                                            title="Descalificar participante"
                                                                        >
                                                                            <i className="bi bi-shield-x me-1"></i>
                                                                            Descalificar
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Paginación */}
                                    <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top flex-wrap gap-2">
                                        <div className="small text-secondary">
                                            Mostrando {startIndex + 1} - {Math.min(startIndex + pageSize, totalRegistries)} de {totalRegistries} registros
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <select 
                                                className="form-select form-select-sm border-secondary-subtle" 
                                                style={{ width: '80px' }}
                                                value={pageSize}
                                                onChange={(e) => {
                                                    setPageSize(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            
                                            <button 
                                                type="button" 
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={verifiedPage === 1}
                                                onClick={() => setCurrentPage(1)}
                                            >
                                                <i className="bi bi-chevron-double-left"></i>
                                            </button>
                                            <button 
                                                type="button" 
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={verifiedPage === 1}
                                                onClick={() => setCurrentPage(verifiedPage - 1)}
                                            >
                                                <i className="bi bi-chevron-left"></i>
                                            </button>
                                            <span className="small fw-semibold text-dark px-2">
                                                Página {verifiedPage} de {totalPages}
                                            </span>
                                            <button 
                                                type="button" 
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={verifiedPage === totalPages}
                                                onClick={() => setCurrentPage(verifiedPage + 1)}
                                            >
                                                <i className="bi bi-chevron-right"></i>
                                            </button>
                                            <button 
                                                type="button" 
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={verifiedPage === totalPages}
                                                onClick={() => setCurrentPage(totalPages)}
                                            >
                                                <i className="bi bi-chevron-double-right"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-5 text-secondary">
                                    <p className="fs-5 mb-0">No se encontraron registros de participación.</p>
                                    <p className="small text-muted">Ajusta los filtros o realiza otra búsqueda.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sección Plegable de Configuración Avanzada */}
                <div className="card border-0 rounded-3 shadow-sm bg-white mt-5">
                    <div 
                        className="card-header bg-light border-0 py-3 d-flex justify-content-between align-items-center cursor-pointer"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                    >
                        <h3 className="h5 fw-bold mb-0 text-dark">
                            <i className="bi bi-gear-wide-connected me-2 text-danger"></i>
                            Configuración avanzada y Zona de Riesgo
                        </h3>
                        <i className={`bi ${isAdvancedOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-5`}></i>
                    </div>
                    {isAdvancedOpen && (
                        <div className="card-body p-4 border-top">
                            <div className="mb-4">
                                <label className="form-label fw-semibold text-secondary d-flex justify-content-between small">
                                    <span>Posición Vertical del Overlay OBS (Offset Y)</span>
                                    <span className="badge bg-secondary">{offsetY}px</span>
                                </label>
                                <input 
                                    type="range" 
                                    className="form-range" 
                                    min="0" 
                                    max="600" 
                                    step="5"
                                    value={offsetY} 
                                    onChange={handlePositionChange} 
                                />
                                <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.7rem' }}>
                                    <span>Abajo (0px)</span>
                                    <span>Arriba (600px)</span>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="form-label fw-semibold text-secondary small d-block mb-2">
                                    Estado de la Trivia para Aficionados
                                </label>
                                <button
                                    type="button"
                                    onClick={() => triggerToggleActiveTrivia(!isTriviaActive)}
                                    className={`btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${
                                        isTriviaActive 
                                        ? 'btn-outline-secondary' 
                                        : 'btn-outline-danger'
                                    }`}
                                >
                                    <i className={`bi ${isTriviaActive ? 'bi-check-circle-fill' : 'bi-slash-circle-fill'}`}></i>
                                    {isTriviaActive ? 'Trivia activa (Recibiendo registros)' : 'Trivia cerrada (Registro bloqueado)'}
                                </button>
                            </div>

                            <hr className="my-4" />

                            <div className="bg-danger-subtle p-3 rounded border border-danger border-opacity-25">
                                <h4 className="fs-6 fw-bold text-danger mb-2">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    Zona Peligrosa
                                </h4>
                                <p className="small text-danger-emphasis mb-3">
                                    Reiniciar la dinámica archivará permanentemente a todos los participantes previos y pondrá en 0 el contador de ganadores de pases dorados en el servidor.
                                </p>
                                <button
                                    type="button"
                                    onClick={triggerResetDynamic}
                                    className="btn btn-danger w-100 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2"
                                >
                                    <i className="bi bi-trash-fill"></i>
                                    Reiniciar dinámica por completo
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </SecondaryBase>
    );
}

export default MvpXolosZoneBoard;
